"use strict";

import Constants from "/js/core/constants.js";
import Analytics from './analytics';

/**
 * Static class holding the '"PrimerMaster.json" data. Has logic to load itself. Static class.
 *
 * <p>Also keeps some hardcoded skill info skilltype css color mappings .
 */
export default class PrimerMaster {

  // Skill type to CSS class mappings
  static _skillTypeToColorClass = { 
    "skilltype-bizop": "cyan",
    "skilltype-brandid": "green",
    "skilltype-marketmeasure": "teal"
  };
  static _skillTypeToDarkColorClass = { 
    "skilltype-bizop": "cyanDark",
    "skilltype-brandid": "greenDark",
    "skilltype-marketmeasure": "tealDark"
  };
  static _skillTypeToBgColorClass = {
    "skilltype-bizop": "bgCyan",
    "skilltype-brandid": "bgGreen",
    "skilltype-marketmeasure": "bgTeal"
  };
  static _skillTypeToBgDarkColorClass = {
    "skilltype-bizop": "bgDarkCyan",
    "skilltype-brandid": "bgDarkGreen",
    "skilltype-marketmeasure": "bgDarkTeal"
  };
  static _skillTypeToLightenedBgColorClass = {
    "skilltype-bizop": "bgLightenedCyan",
    "skilltype-brandid": "bgLightenedGreen",
    "skilltype-marketmeasure": "bgLightenedTeal"
  };
  static _skillTypeToInteractionBgColorClass = {
    "skilltype-bizop": "interactionBgCyan",
    "skilltype-brandid": "interactionBgGreen",
    "skilltype-marketmeasure": "interactionBgTeal"
  }

  // TODO: Consider adding the skill-id as a css class to the element,
  //    and putting the filename and vertical offset values into css classes.
  static _skillIconFilenames = {
    "skill-agencymanage": "agencymanage.svg",
    "skill-analytics": "analytics.svg",
    "skill-bizinsights": "bizinsights.svg",
    "skill-bizmanage": "bizmanage.svg",
    "skill-bizplan": "bizplan.svg",
    "skill-brandbuild": "brandbuild.svg",
    "skill-careerdev": "careerdev.svg",
    "skill-contentmarket": "contentmarket.svg",
    "skill-customerengage": "customerengage.svg",
    "skill-customerservice": "customerservice.svg",
    "skill-digitalmarket1": "digitalmarket1.svg",
    "skill-digitalmarket2": "digitalmarket2.svg",
    "skill-emailmarket": "emailmarket.svg",
    "skill-inclusivity": "inclusivity.svg",
    "skill-mobilemarket": "mobilemarket.svg",
    "skill-moneymanage": "moneymanage.svg",
    "skill-opscommerce": "opscommerce.svg",
    "skill-selling": "selling.svg",
    "skill-socialmedia": "socialmedia.svg",
    "skill-staffing": "staffing.svg",
    "skill-startup": "startup.svg",
    "skill-userexp": "userexp.svg",
    "skill-videomarket": "videomarket.svg",
    "skill-website": "website.svg",
    "skill-workproductivity": "workproductivity.svg"
  }
  static _skillIconOffsets = {
    "skill-agencymanage": -1,
    "skill-analytics": -1,
    "skill-bizinsights": -1,
    "skill-bizmanage": -1,
    "skill-bizplan": -1,
    "skill-brandbuild": -1,
    "skill-careerdev": -2,
    "skill-contentmarket": -1,
    "skill-customerengage": 0,
    "skill-customerservice": -1,
    "skill-digitalmarket1": 0,
    "skill-digitalmarket2": -2,
    "skill-emailmarket": 1,
    "skill-inclusivity": -1,
    "skill-mobilemarket": -2,
    "skill-moneymanage": -1,
    "skill-selling": -1,
    "skill-socialmedia": -1,
    "skill-opscommerce": -2,
    "skill-staffing": -1,
    "skill-startup": -2,
    "skill-userexp": -1,
    "skill-videomarket": 2,
    "skill-website": 0,
    "skill-workproductivity": -1
  };

  static _data = {};  

  /** 
   * Loads the live version of the PrimerMaster json file (the same one
   * that's used by the mobile apps). Required. 
   */
  static load(languageCode, successHandler, failHandler) {
    PrimerMaster._data = "";

    const onSuccess = function(json, textStatus, jqXHR) {
      PrimerMaster._data = json;
      cl('PrimerMaster loaded');
      // Also make map out of lesson array for convenience/performance:
      json.lessonMap = {};
      if (json.lessons) {
        json.lessons.forEach((item) => {
          if (!item.id) {
            return;
          }
          json.lessonMap[item.id] = item;
        });
      }
      successHandler();
    };
    const onError = function(jqXHR, textStatus, errorThrown) {
      Analytics.trackStartupError("primerMaster", jqXHR.status);
      failHandler();
    };
    const path = Constants.getMasterJsonPath(languageCode);
    $.ajax({ url: path, dataType: "json", success: onSuccess, error: onError, context: this });
  }

  static getLessonById(lessonId) {
    return PrimerMaster._data.lessonMap[lessonId];
  }

  static get lessonMap() {
    return PrimerMaster._data.lessonMap;
  }

  static getSkillById(skillId) {
    if (!PrimerMaster._data.skills) {
      return null;
    }
    for (const skill of PrimerMaster._data.skills) {
      if (skill.id === skillId) {
        return skill;
      }
    }
    return null;
  }

  static getSkillForLessonId(lessonId) {
    if (!PrimerMaster._data.skills) {
      return null;
    }
    let result;
    _.every(PrimerMaster._data.skills, function(skill) {
      const has =_.some(skill.lessonIds, function(id) {
        return id == lessonId;
      });
      if (has) {
        result = skill;
      }
      return result == null;
    });
    return result;
  }

  static getSkillLabelForLessonId(lessonId) {
    const skill = this.getSkillForLessonId(lessonId);
    return skill ? skill.label : "";
  }

  static getSkillIconPath(skillId) {
    const fn = this._skillIconFilenames[skillId];
    if (!fn) {
      return null;
    }
    return "img/skill_icons/" + fn;
  }
  /**
   * The skill icon bounding boxes are cropped tight to their content rather than being placed 
   * within a constant-sized bounding box, so need this to make it "appear" more vertically centered ugh.
   */
  static getSkillIconOffset(skillId) {
    const off = this._skillIconOffsets[skillId];
    return off || 0;
  }

  static getBgColorClassForSkill(skillId) {
    const skill = this.getSkillById(skillId);
    if (!skill) {
      return null;
    }
    return this._skillTypeToBgColorClass[skill.skillType];
  }

  /**
   * Returns the name of the css class which applies the appropriate background-color, based on the
   * color theme of the given lesson. 
   */
  static getBgColorClassForLessonId(lessonId) {
    const skill = this.getSkillForLessonId(lessonId);
    if (!skill) {
      return null;
    }
    const colorClass = this._skillTypeToBgColorClass[skill.skillType];
    return colorClass;
  }

  /** 
   * Returns the name of the css class which applies the appropriate color, based on the color theme 
   * of the given lesson. 
   */
  static getColorClassForLessonId(lessonId) {
    const skill = this.getSkillForLessonId(lessonId);
    if (!skill) {
      return null;
    }
    const colorClass = this._skillTypeToColorClass[skill.skillType];
    return colorClass;
  }

  /** 
   * Returns the name of the css class which applies the appropriate color (dark version), based on 
   * the color theme of the given lesson. 
   */
  static getDarkColorClassForLessonId(lessonId) {
    const skill = this.getSkillForLessonId(lessonId);
    if (!skill) {
      return null;
    }
    const colorClass = this._skillTypeToColorClass[skill.skillType];
    return colorClass;
  }

  /** 
   * Returns the name of the css class which applies the appropriate background-color (dark 
   * version), based on the color theme of the given lesson. 
   */
  static getBgDarkColorClassForLessonId(lessonId) {
    const skill = this.getSkillForLessonId(lessonId);
    if (!skill) {
      return null;
    }
    const colorClass = this._skillTypeToBgDarkColorClass[skill.skillType];
    return colorClass;
  }

  /** 
   * Returns the name of the css class which applies the appropriate background-color (lightened
   * version), based on the color theme of the given lesson. 
   */
  static getLightenedBgColorClassForLessonId(lessonId) {
    const skill = this.getSkillForLessonId(lessonId);
    if (!skill) {
      return null;
    }
    const colorClass = this._skillTypeToLightenedBgColorClass[skill.skillType];
    return colorClass;
  }

  static getInteractionBgColorClassForLessonId(lessonId) {
    const skill = this.getSkillForLessonId(lessonId);
    if (!skill) {
      return null;
    }
    const colorClass = this._skillTypeToInteractionBgColorClass[skill.skillType];
    return colorClass;
  }
}
